import { Autocomplete, TextField } from "@mui/material";
import axios from "axios";
import React, { useEffect } from "react";
import { endpoints } from "../../../endpoints/endpoints";
import { useSnackbar } from "notistack";

interface LocationInputProps {
  disabled?: boolean;
  required?: boolean;
  location: {
    label: string;
    value: string;
  } | null;
  setLocation: (location: { label: string; value: string } | null) => void;
  showValidation?: boolean;
  onBlur?: () => void;
}

const LocationInput = ({
  disabled,
  required,
  location,
  setLocation,
  showValidation = true,
  onBlur,
}: LocationInputProps): React.JSX.Element => {
  const { enqueueSnackbar } = useSnackbar();
  const fetching = React.useRef(false);

  const [options, setOptions] = React.useState([]);

  const changeLocation = (
    _event: any,
    option: {
      label: string;
      value: string;
    } | null
  ): void => {
    setLocation(option ?? null);
  };

  const fetchLocationList = React.useCallback(async (): Promise<void> => {
    fetching.current = true;

    axios
      .get(endpoints.location.getAll)
      .then((response) => {
        if (response.data.locations) {
          setOptions(response.data.locations);
        } else {
          enqueueSnackbar("Standortliste konnte nicht geladen werden!", {
            variant: "error",
          });
        }
        fetching.current = false;
      })
      .catch(() => {
        enqueueSnackbar("Standortliste konnte nicht geladen werden!", {
          variant: "error",
        });
        fetching.current = false;
      });
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (options.length === 0 && !fetching.current) {
      fetchLocationList();
    }
  }, [options, fetchLocationList]);

  const isInvalid = !location;
  const showError = showValidation && isInvalid;

  return (
    <Autocomplete
      loading={fetching.current}
      loadingText="Lädt Standortliste..."
      sx={{
        "& .MuiInputBase-root": {
          color: showError
            ? (theme) => theme.palette.error.main
            : isInvalid
              ? undefined
              : (theme) => theme.palette.success.main,
        },
      }}
      disabled={disabled || options.length === 0}
      disablePortal
      id="LocationInput"
      options={options}
      value={location}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onChange={(event, option) => changeLocation(event, option)}
      onBlur={onBlur}
      renderInput={(params) => (
        <TextField
          focused={!!location}
          color="success"
          required={required}
          error={showError}
          helperText={showError ? "Bitte wähle einen Standort aus" : ""}
          {...params}
          label="Standort"
        />
      )}
    />
  );
};

export default LocationInput;
